import type { AiUserTurnStep } from '@/lib/api/ai-user/ai-user-api'

/**
 * Monta o que o usuário de IA lê de uma conversa: só o período de atendimento
 * atual, em texto corrido, com quem falou e a hora.
 *
 * Função pura, num arquivo próprio, para ser testada sem a tela. O texto vai
 * inteiro em cada passo e é debitado dos créditos de IA do tenant, por isso os
 * cortes de tamanho.
 */

export interface ContextMessage {
    id?: number
    role: string
    content: string
    createdAt: string
    name?: string | null
    authorUserId?: number | null
}

export interface ContextChat {
    id: number
    name?: string | null
    /** Cliente cadastrado da conversa: é para ele que a tela de agendamento abre. */
    clientId?: number | null
    currentPeriodStartedAt?: string | null
    /** Canal e última mensagem do contato: a janela de 24 h do WhatsApp na passagem. */
    channel?: string | null
    lastInboundAt?: string | null
    messages: ContextMessage[]
}

/** Quantas mensagens do período entram, no máximo (as mais recentes). */
export const CONTEXT_MAX_MESSAGES = 60
/** Tamanho máximo de uma mensagem no contexto. */
export const CONTEXT_MAX_MESSAGE_CHARS = 1500

const TIME_FORMAT = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
})

/** Papéis que não são conversa: chamada de ferramenta do atendente virtual. */
const HIDDEN_ROLES = new Set(['tool'])

export function speakerLabel(message: ContextMessage, aiUserId: number): string {
    switch (message.role) {
        case 'user':
            return 'Contato'
        case 'assistant':
            return 'Atendente virtual'
        case 'system':
            return 'Sistema'
        case 'company':
            if (message.authorUserId === aiUserId) return 'Você'
            return message.name ? `Equipe (${message.name})` : 'Equipe'
        default:
            return 'Equipe'
    }
}

/** As mensagens do período atual, já sem as que não são conversa. */
export function currentPeriodMessages(chat: ContextChat): ContextMessage[] {
    const start = chat.currentPeriodStartedAt ? new Date(chat.currentPeriodStartedAt).getTime() : null
    return chat.messages.filter(message => {
        if (HIDDEN_ROLES.has(message.role)) return false
        if (start == null) return true
        return new Date(message.createdAt).getTime() >= start
    })
}

/**
 * Falas que não são da equipe: o aviso de sistema ("Atendimento transferido para...") e a
 * IA de triagem (`assistant`). A triagem fica calada nas conversas do usuário de IA, então
 * a fala dela é de ANTES da passagem, e quase sempre é "a equipe vai te informar".
 */
const NOT_TEAM_ROLES = new Set(['system', 'assistant'])

/**
 * Há o que responder: a última fala, tirando o sistema e a triagem, é do contato. Contar a
 * triagem deixava sem resposta a pergunta da conversa movida para a IA depois de a triagem
 * pedir atendimento humano (produção, 28/10/2026). Se a triagem já tiver resolvido, o
 * modelo vê a conversa e decide não responder.
 */
export function lastMessageIsFromContact(chat: ContextChat): boolean {
    const spoken = currentPeriodMessages(chat).filter(message => !NOT_TEAM_ROLES.has(message.role))
    return spoken.length > 0 && spoken[spoken.length - 1].role === 'user'
}

/** Imagens que o modelo lê (a OpenAI não aceita HEIC nem PDF como imagem). */
const IMAGE_URL_PATTERN = /\.(jpe?g|png|webp|gif)(\?|#|$)/i
const ATTACHMENT_PATTERN = /\[Arquivo enviado: ([^\]\s]+)\]/g
/** Quantas imagens do contato vão por passo: cada uma custa tokens de entrada. */
export const CONTEXT_MAX_IMAGES = 4

/**
 * Imagens que o CONTATO mandou no período atual (receita, pedido, documento), das
 * mais recentes, até `CONTEXT_MAX_IMAGES`. O anexo chega na mensagem como
 * `[Arquivo enviado: <url>]`.
 */
export function contactImageUrls(chat: ContextChat, limit = CONTEXT_MAX_IMAGES): string[] {
    const urls: string[] = []
    for (const message of currentPeriodMessages(chat)) {
        if (message.role !== 'user') continue
        for (const match of message.content.matchAll(ATTACHMENT_PATTERN)) {
            if (IMAGE_URL_PATTERN.test(match[1])) urls.push(match[1])
        }
    }
    return [...new Set(urls)].slice(-limit)
}

/** Id da última mensagem do contato no período (marca o que já recebeu decisão). */
export function lastContactMessageId(chat: ContextChat): number | null {
    const messages = currentPeriodMessages(chat)
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        if (messages[index].role === 'user') return messages[index].id ?? null
    }
    return null
}

export function buildConversationTranscript(chat: ContextChat, aiUserId: number): string {
    const messages = currentPeriodMessages(chat).slice(-CONTEXT_MAX_MESSAGES)
    return messages
        .map(message => {
            const when = TIME_FORMAT.format(new Date(message.createdAt))
            const content =
                message.content.length > CONTEXT_MAX_MESSAGE_CHARS
                    ? `${message.content.slice(0, CONTEXT_MAX_MESSAGE_CHARS)} [mensagem cortada]`
                    : message.content
            return `[${when}] ${speakerLabel(message, aiUserId)}: ${content}`
        })
        .join('\n')
}

export interface FunnelStage {
    id: number
    name: string
    sortOrder: number
    isWonStage: boolean
    isLostStage: boolean
    hiddenFromAi: boolean
}

export interface FunnelPipeline {
    id: number
    name: string
    stages: FunnelStage[]
}

export interface FunnelDeal {
    id: number
    title: string
    pipelineId: number
    stageId: number
}

/**
 * Etapas para onde a IA pode levar a oportunidade: as do mesmo funil, menos a atual
 * e as ocultas para a IA. Oportunidade PARADA numa etapa oculta não se move — só
 * uma pessoa ou um fluxo tira o card de lá (mesma regra do backend).
 */
export function allowedTargetStages(deal: FunnelDeal, pipelines: FunnelPipeline[]): FunnelStage[] {
    const pipeline = pipelines.find(candidate => candidate.id === deal.pipelineId)
    if (!pipeline) return []
    const current = pipeline.stages.find(stage => stage.id === deal.stageId)
    if (current?.hiddenFromAi) return []
    return pipeline.stages.filter(stage => stage.id !== deal.stageId && !stage.hiddenFromAi)
}

/** O movimento pedido pelo modelo é um dos oferecidos? Nada fora da lista passa. */
export function isAllowedMove(
    dealId: number,
    stageId: number,
    deals: FunnelDeal[],
    pipelines: FunnelPipeline[],
): boolean {
    const deal = deals.find(candidate => candidate.id === dealId)
    if (!deal) return false
    return allowedTargetStages(deal, pipelines).some(stage => stage.id === stageId)
}

function stageLabel(stage: FunnelStage): string {
    if (stage.isWonStage) return `${stage.name} (ganho)`
    if (stage.isLostStage) return `${stage.name} (perda)`
    return stage.name
}

/** O funil do contato, em texto, para o modelo decidir se a oportunidade muda de etapa. */
export function buildFunnelContext(deals: FunnelDeal[], pipelines: FunnelPipeline[]): string {
    if (deals.length === 0) {
        return 'Este contato não tem oportunidade aberta no seu funil. Não use move_deal_stage.'
    }
    const lines = ['Oportunidades abertas deste contato no seu funil:']
    for (const deal of deals) {
        const pipeline = pipelines.find(candidate => candidate.id === deal.pipelineId)
        const current = pipeline?.stages.find(stage => stage.id === deal.stageId)
        lines.push(
            `- Oportunidade ${deal.id} "${deal.title}", funil "${pipeline?.name ?? deal.pipelineId}", ` +
                `etapa atual "${current?.name ?? deal.stageId}".`,
        )
        const targets = allowedTargetStages(deal, pipelines)
        if (targets.length === 0) {
            lines.push('  Esta oportunidade não pode ser movida por você agora.')
            continue
        }
        lines.push(
            '  Etapas para onde você pode movê-la (id: nome): ' +
                targets.map(stage => `${stage.id}: ${stageLabel(stage)}`).join('; ') +
                '.',
        )
    }
    return lines.join('\n')
}

/** Na passagem o histórico vai inteiro (todos os períodos), das mais recentes para trás, até este tamanho. */
export const HANDOVER_TRANSCRIPT_MAX_CHARS = 30_000
/** Janela do WhatsApp para mensagem livre depois da última mensagem do contato. */
export const WHATSAPP_WINDOW_MS = 24 * 60 * 60 * 1000

/** Histórico de TODOS os períodos da conversa, cortado pelo começo quando passa do tamanho. */
export function buildFullTranscript(chat: ContextChat, aiUserId: number, maxChars = HANDOVER_TRANSCRIPT_MAX_CHARS): string {
    const lines: string[] = []
    let total = 0
    const messages = chat.messages.filter(message => !HIDDEN_ROLES.has(message.role))
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index]
        const when = TIME_FORMAT.format(new Date(message.createdAt))
        const content =
            message.content.length > CONTEXT_MAX_MESSAGE_CHARS
                ? `${message.content.slice(0, CONTEXT_MAX_MESSAGE_CHARS)} [mensagem cortada]`
                : message.content
        const line = `[${when}] ${speakerLabel(message, aiUserId)}: ${content}`
        if (total + line.length > maxChars && lines.length > 0) {
            lines.push('[mensagens mais antigas omitidas]')
            break
        }
        lines.push(line)
        total += line.length + 1
    }
    return lines.reverse().join('\n')
}

/**
 * WhatsApp com a última mensagem do contato há mais de 24 h: mensagem livre não chega.
 *
 * A hora vem da última mensagem do CONTATO no histórico carregado. `Chat.lastInboundAt`
 * é só reserva: em produção ele chega vazio em conversa em que o contato acabou de
 * escrever (28/10/2026, #5062), e tratá-lo como verdade dizia "mais de 24 horas" para
 * quem tinha escrito minutos antes.
 */
export function isWhatsappWindowClosed(chat: ContextChat, now = Date.now()): boolean {
    if (String(chat.channel ?? '').toUpperCase() !== 'WHATSAPP') return false
    let lastInbound = 0
    for (const message of chat.messages) {
        if (message.role !== 'user') continue
        const at = new Date(message.createdAt).getTime()
        if (at > lastInbound) lastInbound = at
    }
    if (chat.lastInboundAt) lastInbound = Math.max(lastInbound, new Date(chat.lastInboundAt).getTime())
    // O contato nunca escreveu: não há janela aberta.
    if (!lastInbound) return true
    return now - lastInbound > WHATSAPP_WINDOW_MS
}

/** Mensagens do contato no período atual escritas depois de `since` (a pergunta ao supervisor). */
export function contactMessagesSince(chat: ContextChat, since: string): ContextMessage[] {
    const cutoff = new Date(since).getTime()
    return currentPeriodMessages(chat).filter(
        message => message.role === 'user' && new Date(message.createdAt).getTime() > cutoff,
    )
}

/**
 * O pedido de um passo em DADOS (`step` de `POST ai-user/turn`). O texto que envolve a
 * conversa mora no banco e é editado no painel MASTER (Integrações > Agente); aqui só se
 * escolhe o passo e se formata a conversa (docs/ai-user.md, "Prompt no banco").
 * - `handover`: a conversa acabou de passar para a IA (ou foi achada por "Procurar
 *   conversas recebidas"); ela lê o histórico inteiro, de todos os períodos, e assume.
 * - `waiting`: há pergunta pendente com o supervisor e o contato escreveu depois dela; o
 *   assunto da pergunta espera, assunto novo é atendido (`holding`: o motivo já teve
 *   aviso ao contato).
 * - `decision`: o passo comum.
 */
export function buildTurnStep(
    chat: ContextChat,
    aiUserId: number,
    params: { handover?: boolean; pending?: { question: string; holding: boolean } | null; context?: string },
): AiUserTurnStep {
    const base = { contactName: chat.name?.trim() || null, context: params.context || null }
    if (params.pending) {
        return {
            ...base,
            kind: 'waiting',
            transcript: buildConversationTranscript(chat, aiUserId),
            pendingQuestion: params.pending.question,
            holding: params.pending.holding,
        }
    }
    if (params.handover) {
        return {
            ...base,
            kind: 'handover',
            transcript: buildFullTranscript(chat, aiUserId),
            whatsappWindowClosed: isWhatsappWindowClosed(chat),
        }
    }
    return { ...base, kind: 'decision', transcript: buildConversationTranscript(chat, aiUserId) }
}
